import styled from 'styled-components';
import React, { FunctionComponent } from 'react';
import { useMedia } from 'react-media-match';
import { DimensionValue } from '@/interfaces';
import { Tooltip } from './Tooltip';
import { ProductSelectionTheme } from '../theme';

type TileProps = Partial<DimensionValue> & { isInline?: boolean; onClick?: () => void };

interface TileLayout {
  colorTile?: boolean;
  rgb?: string;
  image?: string;
  isAvailable?: boolean;
  isInline?: boolean;
  isSelected: boolean;
  theme: ProductSelectionTheme;
}

const TileContainer = styled.div<TileLayout>`
  align-items: center;
  color: ${({ isAvailable = true }) => (isAvailable ? '#333' : '#999')};
  cursor: pointer;
  background: ${({ colorTile = false, rgb, image, theme: { component } }) =>
    colorTile
      ? ` #${rgb} url(${image}) repeat center`
      : component.productSelection.color.background};
  border: 1px solid #cecece;
  background-repeat: no-repeat;
  background-size: cover;
  display: flex;
  ${({ colorTile = false, isInline }) =>
    isInline
      ? 'width: 1.25rem; height: 1.25rem;'
      : `width: ${colorTile ? '2.25rem' : 'auto'}; height: 2.25rem;`}
  line-height: normal;
  justify-content: center;
  ${({ isInline }) => !isInline && 'margin: 0.6rem;'}
  padding: 0.6rem;
  position: relative;
  text-decoration: ${({ isAvailable = true }) => (isAvailable ? 'none' : 'line-through')};
  user-select: none;
  :after {
    content: '';
    display: block;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 10;
    transition: all 400ms ease-in-out;
  }
  :hover:after {
    box-shadow: inset 0 0 0 1px #808080;
  }
  &&.selected:hover:after {
    box-shadow: inset 0 0 0 1px
      ${({ theme: { component } }) => component.productSelection.color.isSelected};
  }
  &&.selected {
    ${({ theme: { component } }) =>
      `box-shadow: 0 0 0 4px ${component.productSelection.color.isSelected};`}
  }
`;

export const Tile: FunctionComponent<TileProps> = ({
  availability = { message: '', state: '' },
  displayText,
  rgb,
  image = null,
  isInline = false,
  isSelected,
  onClick,
}) => {
  const { message, state } = availability;
  const showTooltip = useMedia({ desktop: true, mobile: false, tablet: false });
  return rgb || image ? (
    <TileContainer
      className={isSelected ? 'selected' : ''}
      colorTile
      data-testid="product-selection-tiles-element"
      image={image}
      isInline={isInline}
      isSelected={isSelected}
      onClick={onClick}
      rgb={rgb}
    >
      {displayText && showTooltip && <Tooltip>{displayText}</Tooltip>}
    </TileContainer>
  ) : (
    <TileContainer
      className={isSelected ? 'selected' : ''}
      data-testid="product-selection-tiles-element"
      isAvailable={state !== 'NOT_AVAILABLE'}
      isSelected={isSelected}
      onClick={onClick}
    >
      {displayText}
      {message && showTooltip && <Tooltip>{message}</Tooltip>}
    </TileContainer>
  );
};

export default Tile;
